export const TASK_STATUSES = ['todo', 'in_progress', 'blocked', 'done'] as const;
export type TaskStatus = (typeof TASK_STATUSES)[number];

export const PRIORITIES = ['low', 'medium', 'high', 'critical'] as const;
export type Priority = (typeof PRIORITIES)[number];

export type GroupRole = 'owner' | 'admin' | 'member';

/** The pages a member can be given or kept out of. Settings comes with the role. */
export type Section = 'board' | 'activity' | 'releases';

export type Density = 'comfortable' | 'compact';

/** What a member may see and do, as decided and enforced by the server. */
export type MemberAccess = {
  role: GroupRole;
  sections: Section[];
  abilities: string[];
};

/**
 * The view a member opens a team on. An admin seeds it; the member rearranges it
 * from there, so the two halves are stored separately and merged by the server.
 */
export type MemberLayout = {
  landing: Section;
  /** The board's starting field filter: `all`, `none`, or a field id. */
  boardField: string;
  boardColumns: TaskStatus[];
  density: Density;
};

export type User = { id: string; email: string; displayName: string };

export type Member = User & { role: GroupRole };

/**
 * A member as the admin's team tab sees them: the plain roster plus everything
 * only an admin may read — their access, their fields, and their layout.
 */
export type RosterMember = Member & {
  fieldIds: string[];
  access: MemberAccess;
  /** The starting layout the admin has set for them. */
  layoutDefaults: MemberLayout;
  /** What they actually see now, once their own changes are applied. */
  layout: MemberLayout;
  /** False while they are still on the layout the admin gave them. */
  hasOwnLayout: boolean;
};

export type Group = {
  id: string;
  slug: string;
  name: string;
  role: GroupRole;
  settings?: Record<string, unknown>;
  /** Carried on the list itself, so the router knows where to send this person. */
  access?: MemberAccess;
  layout?: MemberLayout;
};

export type Field = {
  id: string;
  slug: string;
  name: string;
  color: string;
  position: number;
  /** Who new work in this field goes to when the creator names nobody. */
  defaultAssigneeId: string | null;
  // A field's roster is deliberately absent here. It has no revision of its own,
  // so it cannot ride the sync delta without going stale; the page that manages
  // it reads the fields endpoint directly instead.
  deletedAt: string | null;
  updatedAt: string;
  rev: number;
};

/** One inner step of a task. Lives inside the task, never on its own. */
export type ChecklistItem = {
  id: string;
  title: string;
  done: boolean;
};

export type Task = {
  id: string;
  fieldId: string | null;
  versionId: string | null;
  title: string;
  description: string;
  status: TaskStatus;
  priority: Priority;
  checklist: ChecklistItem[];
  assigneeId: string | null;
  createdBy: string | null;
  completedBy: string | null;
  completedAt: string | null;
  /**
   * Fractional, and only meaningful within a column: a card dropped between two
   * others takes the midpoint of their two values.
   */
  position: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  rev: number;
};

export type Version = {
  id: string;
  versionNumber: string;
  title: string;
  description: string;
  status: 'draft' | 'released';
  releasedAt: string | null;
  releasedBy: string | null;
  changelog: string | null;
  taskCount?: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
  rev: number;
};

export type Webhook = {
  id: string;
  label: string;
  isActive: boolean;
  maskedUrl: string;
  lastStatus?: string | null;
  lastAttempt?: string | null;
};

/** A team's self-serve door: the shared code, and whether it currently opens. */
export type JoinCodeInfo = {
  joinCode: string;
  isActive: boolean;
  joinUrl: string;
};

export type Invite = {
  id: string;
  email: string;
  role: GroupRole;
  expiresAt: string;
  createdAt: string;
  invitedBy: string | null;
};

/**
 * The activity statistic, read from the completion log rather than from the
 * tasks — reopening a task erases its `completedAt`, but not its history.
 */
export type Activity = {
  days: number;
  people: Array<{
    userId: string | null;
    displayName: string;
    completed: number;
    reopened: number;
    activeDays: number;
    lastAt: string | null;
  }>;
  /** One row per day in the window, zero-filled, oldest first. */
  daily: Array<{ date: string; completed: number }>;
  fields: Array<{ fieldId: string | null; completed: number }>;
  recent: Array<{
    id: number;
    taskId: string;
    title: string;
    kind: 'completed' | 'reopened';
    userId: string | null;
    fieldId: string | null;
    occurredAt: string;
  }>;
};

export type ReleasePacket = {
  versionNumber: string;
  title: string;
  description: string;
  taskCount: number;
  contributors: string[];
  sections: Array<{
    fieldName: string;
    tasks: Array<{ title: string; priority: string; contributor: string | null }>;
  }>;
};

/**
 * A mutation made locally and not yet confirmed by the server. Creates carry a
 * client-generated id so replaying one after a dropped connection cannot produce
 * a duplicate task.
 */
export type OutboxOp =
  | {
      opId: string;
      kind: 'task.create';
      groupId: string;
      taskId: string;
      payload: Record<string, unknown>;
      attempts: number;
      queuedAt: number;
    }
  | {
      opId: string;
      kind: 'task.update';
      groupId: string;
      taskId: string;
      payload: Record<string, unknown>;
      attempts: number;
      queuedAt: number;
    }
  | {
      opId: string;
      kind: 'task.delete';
      groupId: string;
      taskId: string;
      payload: Record<string, unknown>;
      attempts: number;
      queuedAt: number;
    };

export type SyncState = 'idle' | 'syncing' | 'offline' | 'error';
